import React, { useRef, useCallback, useReducer } from 'react'

import { KEY_VALUES } from '../../constants'
import { COLORS, THEME } from '../../styles'
import { Analytics } from '../../third-parties/analytics'
import { useTranslation } from '../../contexts/I18nContext'

import Styled from './RecipeVideoPlayer.styled'

const formatIngredientName = (name) => {
  if (name?.length) {
    return name.replace(/\(.*\)/g, '').trim()
  }
  return name
}

const formatSteps = ({
  constituents = [],
  directions = [],
  duration = 0,
} = {}) => {
  if (directions.length && duration) {
    const initialTimecode = directions[0].videoTimecodeStart
    const initialStep = initialTimecode
      ? [
          {
            index: 0,
            involvedIngredients: constituents,
            videoTimecodeStart: 0,
            videoTimecodeDuration: initialTimecode,
            videoTimecodeEnd: initialTimecode,
            constituentsCount: constituents.length,
            label: '',
          },
        ]
      : []
    // Check if timecode is set
    const filteredSteps = directions.filter((step) => !!step.videoTimecodeStart)
    return filteredSteps.reduce((steps, step, index) => {
      const isLast = index === filteredSteps.length - 1
      return steps.concat({
        ...step,
        index: index + 1,
        videoTimecodeDuration: isLast
          ? duration - directions[index].videoTimecodeStart
          : directions[index + 1].videoTimecodeStart -
            directions[index].videoTimecodeStart,
        videoTimecodeEnd: isLast
          ? duration
          : directions[index + 1].videoTimecodeStart,
      })
    }, initialStep)
  }
  return []
}

const RecipeVideoPlayer = ({
  additionalConstituents,
  backgroundColor,
  backgroundPatternUrl,
  className,
  constituents,
  coversCount,
  difficulty,
  directions,
  id,
  imageUrl,
  posterUrl,
  pricePerPortionTag,
  textColor,
  title,
  videoUrl,
  onVideoPlayed,
}) => {
  const { t } = useTranslation()
  const videoPlayerRef = useRef()
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      steps: [],
      currentStep: 0,
      isPlaying: false,
      wasPlayed: false,
      forceRestart: false,
      displaySubtitles: false,
    },
  )

  const handlePlayVideo = useCallback(() => {
    if (videoPlayerRef?.current) {
      let displaySubtitles = false
      // Hide every text tracks by default, wait for user to activate
      if (videoPlayerRef.current?.textTracks?.length) {
        displaySubtitles = true
        for (let i = 0; i < videoPlayerRef.current.textTracks.length; i++) {
          videoPlayerRef.current.textTracks[i].mode = 'hidden'
        }
      }

      // Play video
      videoPlayerRef.current.play()
      setState({
        isPlaying: true,
        wasPlayed: true,
        forceRestart: false,
        displaySubtitles,
      })

      // Track first playing
      if (!state.wasPlayed) {
        onVideoPlayed()
        Analytics.track('Played recipe video', { id, title })
      }
    }
  }, [state.wasPlayed, id, title, onVideoPlayed])

  const handleGoToStepClick = useCallback(
    (index) => () => {
      if (videoPlayerRef?.current && state.steps?.[index]) {
        videoPlayerRef.current.currentTime =
          state.steps[index].videoTimecodeStart
        setState({ currentStep: index })
      }
    },
    [state.steps],
  )

  const handlePauseVideo = useCallback(() => {
    if (videoPlayerRef?.current) {
      videoPlayerRef.current.pause()
      setState({ isPlaying: false, forceRestart: true })
      handleGoToStepClick(state.currentStep)()
    }
  }, [handleGoToStepClick, state.currentStep])

  const handleChangeStep = useCallback(
    (event) => {
      const { currentTime } = event.target
      if (
        videoPlayerRef?.current &&
        state.steps[state.currentStep] &&
        currentTime + 0.3 >= state.steps[state.currentStep].videoTimecodeEnd
      ) {
        videoPlayerRef.current.currentTime =
          state.steps[state.currentStep].videoTimecodeStart +
          (state.currentStep === 0 ? 0 : 0.3)
        setState({ currentStep: state.currentStep, forceRestart: true })
      } else {
        setState({ forceRestart: false })
      }
    },
    [state.currentStep, state.steps],
  )

  const handleLoadedMetadata = useCallback(() => {
    if (videoPlayerRef?.current && state.steps.length === 0) {
      setState({
        steps: formatSteps({
          constituents,
          directions,
          duration: videoPlayerRef.current.duration,
        }),
      })
    }
  }, [constituents, state.steps, directions])

  const handleResume = useCallback(() => {
    if (state.steps.length === 0) {
      handleLoadedMetadata()
      handlePlayVideo()
    } else if (state.steps.length > 0) {
      if (state.isPlaying) {
        handlePauseVideo()
      } else {
        handlePlayVideo()
      }
    }
  }, [
    state.steps,
    state.isPlaying,
    handleLoadedMetadata,
    handlePauseVideo,
    handlePlayVideo,
  ])

  return directions.length && videoUrl ? (
    <Styled className={className} wasPlayed={state.wasPlayed}>
      <Styled.videoPlayer wasPlayed={state.wasPlayed}>
        <Styled.videoContainer wasPlayed>
          <Styled.videoWrapper
            posterUrl={state.isPlaying ? undefined : posterUrl}
            backgroundColor={backgroundColor}
            wasPlayed
          >
            <Styled.video
              key={id}
              ref={videoPlayerRef}
              onTimeUpdate={handleChangeStep}
              poster={state.isPlaying ? '' : posterUrl}
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              playsInline
              muted
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
            >
              <source src={videoUrl} type="video/mp4" />
            </Styled.video>
          </Styled.videoWrapper>
          <Styled.videoControls id="video-controls">
            <Styled.videoControl
              position="left"
              disabled={state.currentStep <= 0 || !state.isPlaying}
              onClick={handleGoToStepClick(state.currentStep - 1)}
              data-state="hidden"
            >
              <Styled.videoControlIconContainer
                backgroundColor={THEME.COLORS.foreground.secondary}
              >
                <Styled.videoControlIcon
                  position="left"
                  name="direction-left-16"
                  fill={COLORS.white}
                  width={32}
                />
              </Styled.videoControlIconContainer>
            </Styled.videoControl>
            <Styled.videoPlay
              role="button"
              tabIndex={0}
              onKeyPress={(event) => {
                if (event.key === KEY_VALUES.ENTER) {
                  handleResume()
                }
              }}
              onClick={handleResume}
              isPlaying={state.isPlaying}
              id="playpause"
              data-state="play"
            >
              <Styled.videoResumeButton isPlaying={state.isPlaying}>
                {state.steps.length > 0 ? (
                  <Styled.videoResumeButtonIcon
                    name={state.isPlaying ? 'pause-fill-32' : 'play-fill-32'}
                    width={36}
                    height={36}
                    fill={COLORS.white}
                  />
                ) : (
                  <Styled.videoResumeButtonSpinner
                    width={36}
                    fill={COLORS.white}
                  />
                )}
              </Styled.videoResumeButton>
            </Styled.videoPlay>
            <Styled.videoControl
              position="right"
              disabled={
                state.currentStep >= state.steps.length - 1 || !state.isPlaying
              }
              onClick={handleGoToStepClick(state.currentStep + 1)}
              data-state="hidden"
            >
              <Styled.videoControlIconContainer
                backgroundColor={backgroundColor}
              >
                <Styled.videoControlIcon
                  position="right"
                  name="direction-left-16"
                  fill={textColor}
                  width={32}
                />
              </Styled.videoControlIconContainer>
            </Styled.videoControl>
            {state.displaySubtitles ? (
              <Styled.subtitles
                id="subtitles"
                onClick={() => {}}
                disabled
                type="button"
                data-state="subtitles"
              >
                <Styled.sutitleIcon
                  name="subtitles-24"
                  fill={COLORS.white}
                  width={24}
                  height={24}
                />
              </Styled.subtitles>
            ) : null}
          </Styled.videoControls>

          <Styled.videoBench>
            {state.steps.map((step, index) => (
              <Styled.videoBenchStep
                key={step.index}
                currentActive={state.isPlaying && state.currentStep === index}
                active={index < state.currentStep}
                count={state.steps.length}
                onClick={handleGoToStepClick(step.index)}
                tabIndex={-1}
              >
                <Styled.videoBenchProgress
                  active={
                    state.isPlaying &&
                    index === state.currentStep &&
                    !state.forceRestart
                  }
                  passed={index < state.currentStep}
                  duration={step.videoTimecodeDuration}
                />
              </Styled.videoBenchStep>
            ))}
          </Styled.videoBench>
        </Styled.videoContainer>
        <Styled.step backgroundColor={backgroundColor}>
          {state.steps.length && state.steps[state.currentStep] ? (
            <Styled.stepContent>
              <Styled.stepTitle textColor={textColor}>
                {state.steps[state.currentStep].constituentsCount
                  ? t(
                      'RECIPE.TAKE_INGREDIENTS',
                      state.steps[state.currentStep].constituentsCount || '',
                    )
                  : state.steps[state.currentStep].label}
              </Styled.stepTitle>
              {state.steps[state.currentStep].involvedIngredients.length ? (
                <Styled.stepIngredients>
                  {state.steps[state.currentStep].involvedIngredients.map(
                    (ingredient) => {
                      const constituent =
                        (constituents &&
                          constituents.find((c) => c.id === ingredient.id)) ||
                        {}
                      const additionalConstituent =
                        (additionalConstituents &&
                          additionalConstituents.find(
                            (c) => c?.ingredient?.id === ingredient.id,
                          )) ||
                        {}
                      const involvedIngredient = {
                        ...ingredient,
                        ...constituent,
                        ...additionalConstituent,
                      }
                      return (
                        <Styled.stepIngredient
                          size="small"
                          key={involvedIngredient.id}
                          name={formatIngredientName(involvedIngredient.name)}
                          alt={involvedIngredient.name}
                          imageUrl={involvedIngredient.imageUrl}
                          variant={
                            textColor === COLORS.black ? 'main' : 'white'
                          }
                          quantityPerCover={involvedIngredient.quantityPerCover}
                          unit={involvedIngredient.unit}
                          isOptional={involvedIngredient.isOptional}
                          coversCount={coversCount}
                          additionalMeasures={
                            involvedIngredient.additionalMeasures
                          }
                        />
                      )
                    },
                  )}
                </Styled.stepIngredients>
              ) : null}
            </Styled.stepContent>
          ) : null}
        </Styled.step>
      </Styled.videoPlayer>

      {state.wasPlayed ? null : (
        <Styled.recipePoster
          backgroundColor={backgroundColor}
          backgroundPatternUrl={backgroundPatternUrl}
          difficulty={difficulty}
          imageUrl={imageUrl}
          onPlayClick={handleResume}
          pricePerPortionTag={pricePerPortionTag}
          title={title}
        />
      )}
    </Styled>
  ) : null
}

RecipeVideoPlayer.defaultProps = {
  directions: [],
  constituents: [],
  additionalConstituents: [],
}

export default RecipeVideoPlayer
